import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import ToggleButton from 'common/ToggleButton/ToggleButton'
import Styles from './ScheduledPostTargetsPreview.module.scss'
import cx from 'classnames'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { ProviderToName as BaseProviderToName } from 'components/ArticlePostPreview/PostPreviewBody/IntegrationsDrawer/constants'
import { Text } from 'blaze-ui/Text/Text'
import { InstagramBrand } from 'blaze-ui/icons/24'
import { FacebookBrand } from 'blaze-ui/icons/35'
import { TwitterBrand, Lock3, LinkAngled, Send1, EyeOpen, ArrowRotateLeft2 } from 'blaze-ui/icons/20'
import { LinkedInBrand } from 'blaze-ui/icons/32'
import { TikTokBrand, YouTubeBrand, AudioSettings, ChevronDown2 } from 'blaze-ui/icons'
import ConnectButton from './ConnectButton/ConnectButton'
import { Spinner } from 'common/Spinner/Spinner'
import { InitiateOauthTrigger } from 'components/ConnectIntegrationTrigger/InitiateOauthTrigger'
import { calendarKeys } from 'hooks/queries/calendar/calendarKeys'
import { useQueryClient } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useAddAdditionalAccount } from 'hooks/useAddAdditionalAccount'
import { useConnectAccountPopup } from 'components/Onboarding/useConnectAccountPopup'
import RetryButton from './RetryButton/RetryButton'
import ViewPostButton from './ViewPostButton/ViewPostButton'
import PostNowButton from './PostNowButton/PostNowButton'
import { usePublishScheduledPostTargets } from 'hooks/queries/scheduledPostTargets/usePublishScheduledPostTargets'
import useOpenWindowSafely from 'hooks/useOpenWindowSafely'
import { getTargetActionConfig } from './utils/getTargetActionConfig'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { PostSettingsModal } from 'components/PostSettingsModal'
import { useModals } from 'almanac-ui/Modal'
import { getChannelForPlatform } from 'screens/Calendar/FullScreenPreview/utils'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { Pressable } from '@react-aria/interactions'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { MobileActionButton } from './MobileActionButton/MobileActionButton'
import { noop } from 'lodash'
import { MobileScheduledPostTargetPreviewItem } from './MobileScheduledPostTargetPreviewItem/MobileScheduledPostTargetPreviewItem'
import { IconWithStatus } from './IconWithStatus/IconWithStatus'
import { useScheduledPostTargets } from 'hooks/queries/scheduledPostTargets'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'
import { IntegrationSelector } from './IntegrationSelector/IntegrationSelector'
import { useCreateScheduledPostTarget } from 'hooks/queries/scheduledPostTargets/useCreateScheduledPostTarget'
import { useDeleteScheduledPostTarget } from 'hooks/queries/scheduledPostTargets/useDeleteScheduledPostTarget'
import { useState } from 'react'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'

const ProviderToName = {
  ...BaseProviderToName,
  [ExternalIntegrationEnum.X]: 'X / Twitter',
}

export interface ValidationResult {
  isValid: boolean
  errors: string[]
}

export const providerIcon: Partial<Record<ExternalIntegrationEnum, JSX.Element>> = {
  instagram: <InstagramBrand />,
  linkedin: <LinkedInBrand size={24} />,
  x: <TwitterBrand size={24} />,
  facebook: <FacebookBrand size={24} />,
  tiktok: <TikTokBrand size={24} />,
  youtube: <YouTubeBrand size={24} />,
}

export const providerIconV2: Partial<Record<ExternalIntegrationEnum, JSX.Element>> = {
  instagram: <InstagramBrand size={20} />,
  linkedin: <LinkedInBrand size={20} />,
  x: <TwitterBrand size={20} />,
  facebook: <FacebookBrand size={20} />,
  tiktok: <TikTokBrand size={20} />,
  youtube: <YouTubeBrand size={20} />,
}

interface ScheduledPostTargetPreviewItemProps {
  platform: ExternalIntegrationEnum
  almanacFile: AlmanacFileShowItem
  isAvailable: boolean
  onToggle: (platform: ExternalIntegrationEnum, isChecked: boolean) => void
  onCreateAndPublish?: (platform: ExternalIntegrationEnum) => void
  isActive?: boolean
  onPress: () => void
  isDisabled?: boolean
  validation: ValidationResult
  isLoading?: boolean
  isAlreadyPosted?: boolean
  needsReauth?: boolean
  isLocked?: boolean
  isLastCheckedItem?: boolean
}

export const ScheduledPostTargetPreviewItem: React.FC<ScheduledPostTargetPreviewItemProps> = ({
  platform,
  almanacFile,
  isAvailable,
  onToggle,
  onCreateAndPublish,
  isActive = false,
  onPress,
  isDisabled = false,
  validation,
  isLoading = false,
  isAlreadyPosted = false,
  needsReauth = false,
  isLocked = false,
  isLastCheckedItem = false,
}) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const openWindowSafely = useOpenWindowSafely()
  const { openModal } = useModals()
  const approvalsEnabled = useFlipperFlag('post_approvals')
  const { isMobile } = useResponsiveDesign()
  const multipleAccountsEnabled = useFlipperFlag('multiple_accounts')
  const createTargetMutation = useCreateScheduledPostTarget()
  const deleteTargetMutation = useDeleteScheduledPostTarget()
  const [countHovered, setCountHovered] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const onConnect = useAddAdditionalAccount({
    provider: platform,
    onConnectSuccess: () => {
      queryClient.invalidateQueries(calendarKeys.all)
      queryClient.invalidateQueries(['externalIntegrations', workspaceId])
    },
  })

  const { connect: connectAccountPopup } = useConnectAccountPopup()
  const onRetryAuth = (popup?: Window) => {
    connectAccountPopup(popup, {
      onConnect: () => {
        queryClient.invalidateQueries(calendarKeys.all)
        queryClient.invalidateQueries(['externalIntegrations', workspaceId])
      },
    })
  }

  const { data: targets = [] } = useScheduledPostTargets(almanacFile.scheduled_post?.id, {
    enablePolling: true,
  })
  const target = targets.find((t) => t.provider === platform)
  const hasTarget = !!target

  const { mutate: publishScheduledPostTargets } = usePublishScheduledPostTargets()
  const { data: externalIntegrations } = useExternalIntegrations()

  const existingIntegrations = externalIntegrations?.external_integrations.filter(
    (integration) => integration.provider === platform,
  )
  const existingIntegration = existingIntegrations?.find(
    (integration) => integration.id === target?.external_integration_id,
  )
  const hasMultipleIntegrations = multipleAccountsEnabled && existingIntegrations && existingIntegrations.length > 1

  const selectedIntegrationIds = targets
    .filter((t) => t.provider === platform && t.external_integration_id)
    .map((t) => t.external_integration_id)

  const onReauth = async (platform: ExternalIntegrationEnum) => {
    const accountToRefreshId = existingIntegration?.id

    if (accountToRefreshId) {
      localStorage.setItem(`${platform}_accountToRefreshId`, accountToRefreshId.toString())
    }
  }

  const actionConfig = getTargetActionConfig({
    platform,
    target,
    isAvailable,
    validation,
    isLoading,
    isAlreadyPosted,
    isDisabled,
    hasTarget,
    needsReauth,
    onConnect: () => {
      // This will be used as a callback for InitiateOauthTrigger
    },
    onToggle,
    onPublish: (targetId) =>
      target?.scheduled_post_id &&
      publishScheduledPostTargets({ scheduledPostId: target.scheduled_post_id, targetIds: [targetId] }),
    onPublishWithoutTarget: () => {
      // Create the target and immediately publish it
      onCreateAndPublish?.(platform) || onToggle(platform, true)
    },
    onViewPost: (url) => openWindowSafely(url),
    onRetry: (targetId) =>
      target?.scheduled_post_id &&
      publishScheduledPostTargets({ scheduledPostId: target.scheduled_post_id, targetIds: [targetId] }),
    onReauth,
    isLocked,
  })

  const getActionElement = () => {
    switch (actionConfig.type) {
      case 'loading':
        return (
          <div>
            <Spinner />
          </div>
        )

      case 'posting':
        return <span className={Styles.label}>Posting</span>

      case 'locked':
        if (isMobile) {
          return (
            <div className={Styles.toggleButton}>
              <IconButton variant="ghost" color="var(--dark-25)" isDisabled size={'sm'} icon={Lock3} />
              <ToggleButton
                name={`${platform}-locked-toggle`}
                checked={false}
                disabled={true}
                handleChange={noop}
                size={'sm-md'}
                isAPMobileWeb
              />
            </div>
          )
        }
        return (
          <IconButton
            variant="ghost"
            color="var(--dark-80)"
            isDisabled
            size={approvalsEnabled ? 'xs' : 'sm'}
            icon={Lock3}
          />
        )

      case 'connect':
        if (isDisabled) {
          return <></>
        }
        if (isMobile) {
          return (
            <InitiateOauthTrigger popUp provider={platform} onInitiatedOauth={onConnect}>
              <MobileActionButton onPress={actionConfig.onPress} label="Connect" icon={LinkAngled} />
            </InitiateOauthTrigger>
          )
        }
        if (approvalsEnabled) {
          return <span className={Styles.label}>Connect</span>
        }
        return (
          <InitiateOauthTrigger popUp provider={platform} onInitiatedOauth={onConnect}>
            <ConnectButton />
          </InitiateOauthTrigger>
        )

      case 'post-now':
        if (isMobile) {
          return <MobileActionButton onPress={actionConfig.onPress} label="Post Now" icon={Send1} />
        }
        if (approvalsEnabled) {
          return <span className={Styles.label}>Post Now</span>
        }
        return <PostNowButton onPress={actionConfig.onPress} disabled={actionConfig.disabled} />

      case 'view-post':
        if (isMobile) {
          return <MobileActionButton onPress={actionConfig.onPress} label="View Post" icon={EyeOpen} />
        }
        if (approvalsEnabled) {
          return <span className={Styles.label}>View</span>
        }
        return <ViewPostButton onPress={actionConfig.onPress} />

      case 'retry-auth':
        if (isMobile) {
          return (
            <InitiateOauthTrigger
              popUp
              provider={platform}
              onInitiatedOauth={onRetryAuth}
              accountToRefreshId={existingIntegration?.id}
            >
              <MobileActionButton onPress={actionConfig.onPress} label="Retry" icon={ArrowRotateLeft2} />
            </InitiateOauthTrigger>
          )
        }
        if (approvalsEnabled) {
          return <span className={cx(Styles.label, Styles.errorLabel)}>Retry</span>
        }
        return (
          <InitiateOauthTrigger
            popUp
            provider={platform}
            onInitiatedOauth={onRetryAuth}
            accountToRefreshId={existingIntegration?.id}
          >
            <RetryButton onPress={actionConfig.onPress} />
          </InitiateOauthTrigger>
        )

      case 'retry':
        if (isMobile) {
          return <MobileActionButton onPress={actionConfig.onPress} label="Retry" icon={ArrowRotateLeft2} />
        }
        if (approvalsEnabled) {
          return <span className={cx(Styles.label, Styles.errorLabel)}>Retry</span>
        }
        return <RetryButton onPress={actionConfig.onPress} />

      case 'toggle':
      default:
        const desktopIconSize = approvalsEnabled ? 'xs' : 'sm-md'
        const iconSize = isMobile ? 'sm-md' : desktopIconSize

        const handleToggleClick = (e: React.ChangeEvent<HTMLInputElement>) => {
          e.stopPropagation()
          if (hasMultipleIntegrations) {
            setIsMenuOpen(true)
          } else {
            actionConfig.onPress?.()
          }
        }
        return (
          <div className={Styles.toggleButton} style={hasMultipleIntegrations ? { pointerEvents: 'none' } : undefined}>
            <ToggleButton
              name={`${platform}-toggle`}
              checked={hasTarget}
              disabled={actionConfig.disabled}
              handleChange={handleToggleClick}
              size={iconSize}
              isAPMobileWeb={isMobile}
            />
          </div>
        )
    }
  }

  const handlePostSettingsClick = () => {
    if (!target?.scheduled_post_id || !target?.id) return

    const channel = getChannelForPlatform(platform)

    if (!channel) return
    openModal(PostSettingsModal, {
      channel,
      scheduledPostId: target.scheduled_post_id,
      scheduledPostTargetId: target.id,
    })
  }

  const getButtonTooltipLabel = () => {
    return actionConfig.buttonTooltip || ''
  }

  const shouldShowButtonTooltip = () => {
    return !!actionConfig.buttonTooltip
  }

  const getComponentTooltipLabel = () => {
    return actionConfig.componentTooltip || ''
  }

  const shouldShowComponentTooltip = () => {
    return !!actionConfig.componentTooltip
  }

  if (isMobile) {
    return (
      <MobileScheduledPostTargetPreviewItem
        getActionElement={getActionElement}
        needsReauth={needsReauth}
        platform={platform}
        providerIcon={providerIcon[platform]}
        target={target}
        validation={validation}
      />
    )
  }

  const handleIntegrationSelect = async (integrationId: number) => {
    if (!almanacFile.scheduled_post?.id) return

    const scheduledPostId = almanacFile.scheduled_post.id

    const existingTarget = targets.find((t) => t.provider === platform && t.external_integration_id === integrationId)
    const integration = externalIntegrations?.external_integrations.find((ei) => ei.id === integrationId)

    if (existingTarget) {
      await deleteTargetMutation.mutateAsync({
        scheduled_post_id: scheduledPostId,
        target_id: existingTarget.id,
        integration_name: integration?.name,
      })
    } else {
      await createTargetMutation.mutateAsync({
        scheduled_post_id: scheduledPostId,
        provider: platform,
        external_integration_id: integrationId,
        integration_name: integration?.name,
      })
    }
  }

  const showStatusOverlay =
    target?.status === 'failed' || !validation.isValid || target?.status === 'published' || needsReauth

  return (
    <ConditionalWrapper
      condition={approvalsEnabled && ['connect', 'retry-auth'].includes(actionConfig.type)}
      wrapper={(children) => (
        <InitiateOauthTrigger
          popUp
          provider={platform}
          onInitiatedOauth={actionConfig.type === 'retry-auth' ? onRetryAuth : onConnect}
          accountToRefreshId={actionConfig.type === 'retry-auth' ? existingIntegration?.id : undefined}
        >
          <Pressable>{children}</Pressable>
        </InitiateOauthTrigger>
      )}
    >
      <div
        data-testid={`${platform}-platform`}
        className={cx(Styles.platformItem, {
          [Styles.active]: isActive && !approvalsEnabled,
          [Styles.showStatusOverlay]: showStatusOverlay,
        })}
        onClick={(e) => {
          if (hasMultipleIntegrations) {
            setIsMenuOpen(true)
            setCountHovered(false)
            return
          }
          if (approvalsEnabled) {
            actionConfig.onPress?.()
          } else {
            onPress()
          }
        }}
        onMouseEnter={() => setCountHovered(true)}
        onMouseLeave={() => setCountHovered(false)}
      >
        <Tooltip label={getComponentTooltipLabel()} hide={!shouldShowComponentTooltip()}>
          <div
            className={cx(Styles.iconWithPlatformName, { [Styles.connect]: actionConfig.type === 'connect' })}
            data-testid={`${platform}-icon-with-platform-name`}
          >
            <IconWithStatus
              providerIcon={isSystemRedesignEnabled ? providerIconV2[platform] : providerIcon[platform]}
              target={target}
              validation={validation}
              needsReauth={needsReauth}
            />
            {hasMultipleIntegrations && !showStatusOverlay ? (
              <IntegrationSelector
                integrations={existingIntegrations}
                selectedIntegrationIds={selectedIntegrationIds}
                onSelect={handleIntegrationSelect}
                onAddNewAccount={onConnect}
                platform={platform}
                isOpen={isMenuOpen}
                onOpenChange={setIsMenuOpen}
                disableLastItem={isLastCheckedItem && selectedIntegrationIds.length === 1}
                trigger={
                  <div className={Styles.integrationCount}>
                    {countHovered ? (
                      <ChevronDown2 size={16} color="var(--light-100)" />
                    ) : (
                      <Text variant="label" color="var(--light-100)">
                        {selectedIntegrationIds.length}
                      </Text>
                    )}
                  </div>
                }
              />
            ) : undefined}
            {!approvalsEnabled && (
              <Text variant="smallList" color="var(--dark-80)">
                {ProviderToName[platform]}
              </Text>
            )}
          </div>
        </Tooltip>
        <div className={Styles.postSettingsButtonGroup}>
          {actionConfig.showPostSettings && (
            <IconButton
              onPress={handlePostSettingsClick}
              variant="ghost"
              className={Styles.postSettingsButton}
              color="var(--dark-80)"
              icon={AudioSettings}
              data-testid="post-settings-button"
              isDisabled={isLoading || !hasTarget}
            />
          )}
          <Tooltip label={getButtonTooltipLabel()} hide={!shouldShowButtonTooltip()}>
            <div>{getActionElement()}</div>
          </Tooltip>
        </div>
      </div>
    </ConditionalWrapper>
  )
}
